import { React, useState, useEffect, useCallback } from "react"
import {
  Briefcase,
  MapPin,
  Calendar,
  X,
  Clock,
  GraduationCap,
  Mail,
  Phone,
  Globe,
  Award,
  CheckCircle,
} from "lucide-react"
import BTCConverter from "../../BTCConverter"
import styles from "../../../components/ui/dialog.module.css"
import { ApplicationModal } from "./ApplicationModal"
import { useCheckApplicationStatus } from "../../../hooks/DataFetching/useCheckApplicationStatus"
import {
  formatJobDate,
  formatJobLocation,
  getJobApplicationUnavailableReason,
  isJobAcceptingApplications,
  valueOrFallback,
} from "./jobDisplay"

export const JobModal = ({ job, isOpen, onClose }) => {
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false)
  const [shouldCheckStatus, setShouldCheckStatus] = useState(true)
  const { applicationStatus } = useCheckApplicationStatus(job && shouldCheckStatus ? [job.id] : [])

  const checkStatus = useCallback(() => {
    if (shouldCheckStatus) {
      // Perform the status check
      setShouldCheckStatus(false)
    }
  }, [shouldCheckStatus])

  useEffect(() => {
    if (isOpen) {
      setShouldCheckStatus(true)
    }
  }, [isOpen])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  if (!job || !isOpen) return null

  const handleApplyNow = () => {
    setIsApplicationModalOpen(true)
  }

  const hasApplied = applicationStatus[job.id]
  const canApply = isJobAcceptingApplications(job)

  return (
    <>
      <div className={styles.dialogOverlay} onClick={onClose}>
        <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.dialogHeader}>
            <h2 className={styles.dialogTitle}>{job.title}</h2>
            <p className={styles.dialogDescription}>{valueOrFallback(job.groupName, "Group not specified")}</p>
            <button onClick={onClose} className={styles.closeButton} aria-label="Close">
              <X size={24} />
            </button>
          </div>
          <div className={styles.dialogBody}>
            <div className={styles.fieldGroup}>
              <span className={styles.badge}>
                <Briefcase size={14} />
                {valueOrFallback(job.employmentType)}
              </span>
              <span className={styles.badge}>
                <MapPin size={14} />
                {formatJobLocation(job)}
              </span>
              <span className={styles.badge}>
                <Calendar size={14} />
                Posted {formatJobDate(job.createdOn)}
              </span>
              <span className={styles.badge}>
                <Clock size={14} />
                {valueOrFallback(job.experienceLevel)}
              </span>
              {hasApplied && (
                <span className={`${styles.badge} ${styles.appliedBadge}`}>
                  <CheckCircle size={14} />
                  Applied
                </span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabel}>Description</div>
              <div className={styles.fieldValue}>{valueOrFallback(job.positionDescription)}</div>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabel}>Duties</div>
              <div className={styles.fieldValue}>{valueOrFallback(job.positionDuties)}</div>
            </div>

            <div className={styles.gridContainer}>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Compensation (weekly)</div>
                <div className={styles.fieldValue}>
                  {job.weeklyCompensation ? (
                    <BTCConverter value={job.weeklyCompensation} fontType="Arial" fontSize="16px" height="50px" />
                  ) : (
                    "Compensation not specified"
                  )}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Remote Option</div>
                <div className={styles.fieldValue}>
                  <span className={styles.inlineIcon}>
                    <Globe size={14} />
                  </span>
                  {job.remoteOption ? "Yes" : "No"}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Education</div>
                <div className={styles.fieldValue}>
                  <span className={styles.inlineIcon}>
                    <GraduationCap size={14} />
                  </span>
                  {valueOrFallback(job.education)}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Experience Level</div>
                <div className={styles.fieldValue}>
                  <span className={styles.inlineIcon}>
                    <Award size={14} />
                  </span>
                  {valueOrFallback(job.experienceLevel)}
                </div>
              </div>

              <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                <div className={styles.fieldLabel}>Skills</div>
                <div className={styles.fieldValue}>{valueOrFallback(job.freeTextSkills)}</div>
              </div>

              <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                <div className={styles.fieldLabel}>Benefits</div>
                <div className={styles.fieldValue}>{valueOrFallback(job.benefits)}</div>
              </div>

              <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                <div className={styles.fieldLabel}>Keywords</div>
                <div className={styles.fieldValue}>{valueOrFallback(job.keywords)}</div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Important Dates</div>
                <div className={styles.fieldValue}>
                  <div>Closing: {formatJobDate(job.closingDate)}</div>
                  <div>Hire: {formatJobDate(job.hireDate)}</div>
                  {job.endDate && <div>End: {formatJobDate(job.endDate)}</div>}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>Contact Information</div>
                <div className={styles.fieldValue}>
                  <div>{valueOrFallback(job.contactName, "Contact name not specified")}</div>
                  <div>
                    <span className={styles.inlineIcon}>
                      <Mail size={14} />
                    </span>
                    {valueOrFallback(job.contactEmail, "Contact email not specified")}
                  </div>
                  <div>
                    <span className={styles.inlineIcon}>
                      <Phone size={14} />
                    </span>
                    {valueOrFallback(job.contactPhone, "Contact phone not specified")}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.dialogFooter}>
            <button onClick={onClose} className={styles.cancelButton}>
              Close
            </button>
            {hasApplied ? (
              <button className={`${styles.applyButton} ${styles.appliedButton}`} disabled>
                Already Applied
              </button>
            ) : !canApply ? (
              <button className={`${styles.applyButton} ${styles.appliedButton}`} disabled>
                {getJobApplicationUnavailableReason(job)}
              </button>
            ) : (
              <button onClick={handleApplyNow} className={styles.applyButton}>
                Apply Now
              </button>
            )}
          </div>
        </div>
      </div>
      <ApplicationModal
        isOpen={isApplicationModalOpen}
        onClose={() => setIsApplicationModalOpen(false)}
        positionId={job.id}
      />
    </>
  )
}

